import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, CheckCircle, Users, Activity, TrendingUp, Network, ArrowRight } from "lucide-react";
import { useProjectStats } from "@/hooks/useProjectStats";
import { useProjectActivity } from "@/hooks/useProjectActivity";
import { useMembers } from "@/hooks/useMembers";
import { formatRelativeTime, formatDateTime } from "@/lib/date";
import type { ActivityEvent } from "@/hooks/useProjectActivity";

function StatsSkeleton() {
  return (
    <div data-testid="stats-skeleton" className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div
            key={i}
            className="h-28 animate-pulse rounded-lg border bg-muted/50"
          />
        ))}
      </div>
      <div className="h-48 animate-pulse rounded-lg border bg-muted/50" />
    </div>
  );
}

function ProgressBar({ value, className }: { value: number; className?: string }) {
  return (
    <div className="mt-3 h-2 w-full rounded-full bg-muted">
      <div
        className={`h-2 rounded-full transition-all ${className ?? "bg-primary"}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

function colorForPercent(pct: number): string {
  if (pct >= 80) return "text-green-500";
  if (pct >= 50) return "text-yellow-500";
  return "text-red-500";
}

function barColorForPercent(pct: number): string {
  if (pct >= 80) return "bg-green-500";
  if (pct >= 50) return "bg-yellow-500";
  return "bg-red-500";
}

function StatCard({
  icon: Icon,
  label,
  value,
  detail,
  percent,
}: {
  icon: typeof Shield;
  label: string;
  value: string;
  detail?: string;
  percent?: number;
}) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </div>
      <p className={`mt-3 text-3xl font-bold ${percent != null ? colorForPercent(percent) : ""}`}>
        {value}
      </p>
      {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
      {percent != null && <ProgressBar value={percent} className={barColorForPercent(percent)} />}
    </div>
  );
}

function RecentActivityPreview({ events }: { events: ActivityEvent[] }) {
  const { t } = useTranslation();

  if (events.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        {t("projectPortal.activity.empty", "No activity yet")}
      </p>
    );
  }

  return (
    <ul className="divide-y">
      {events.slice(0, 5).map((event) => (
        <li key={event.event_id ?? event.recorded_at} className="flex items-center justify-between gap-2 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{event.title}</p>
            <p className="truncate text-xs text-muted-foreground">{event.actor}</p>
          </div>
          <time
            dateTime={event.recorded_at}
            title={formatDateTime(event.recorded_at)}
            className="shrink-0 text-xs text-muted-foreground"
          >
            {formatRelativeTime(event.recorded_at)}
          </time>
        </li>
      ))}
    </ul>
  );
}

export function ProjectOverviewPanel() {
  const { orgSlug = "", projectSlug = "" } = useParams<{ orgSlug: string; projectSlug: string }>();
  const { t } = useTranslation();
  const { stats, loading, error } = useProjectStats(projectSlug);
  const { events, loading: activityLoading } = useProjectActivity(projectSlug);
  const { members } = useMembers();
  const memberNames = new Map(members.map((m) => [m.id, m.name || m.email]));

  if (loading) return <StatsSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12">
        <p className="text-sm text-destructive">
          {t("projectPortal.overview.loadError")}
        </p>
      </div>
    );
  }

  if (!stats) return null;

  const coveragePct = stats.artifact_coverage_pct;
  const totalGates =
    stats.gate_pass_rate.pass_count +
    stats.gate_pass_rate.fail_count +
    stats.gate_pass_rate.pending_count;
  const gatePct =
    totalGates === 0
      ? 0
      : Math.round((stats.gate_pass_rate.pass_count / totalGates) * 100);
  const gateText = totalGates === 0 ? "--" : `${gatePct}%`;
  const gateDetail =
    totalGates === 0
      ? undefined
      : `${stats.gate_pass_rate.pass_count}/${totalGates} ${t("projectPortal.overview.passing")}`;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={Shield}
          label={t("projectPortal.overview.artifactCoverage")}
          value={`${coveragePct}%`}
          percent={coveragePct}
        />
        <StatCard
          icon={CheckCircle}
          label={t("projectPortal.overview.gatePassRate")}
          value={gateText}
          detail={gateDetail}
          percent={totalGates > 0 ? gatePct : undefined}
        />
        <StatCard
          icon={Users}
          label={t("projectPortal.overview.activeMembers")}
          value={String(stats.adoption.active_member_count)}
        />
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex items-center gap-2 border-b px-5 py-3">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">
            {t("projectPortal.overview.recentActivity", "Recent Activity")}
          </h3>
        </div>
        <div className="px-5">
          {activityLoading ? (
            <div className="space-y-3 py-4">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="h-8 animate-pulse rounded bg-muted/50" />
              ))}
            </div>
          ) : (
            <RecentActivityPreview events={events} />
          )}
        </div>
      </div>

      {stats.adoption.members.length > 0 && (
        <div className="rounded-lg border bg-card">
          <div className="flex items-center gap-2 border-b px-5 py-3">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">
              {t("projectPortal.overview.teamActivity", "Team Activity")}
            </h3>
          </div>
          <ul className="divide-y px-5">
            {stats.adoption.members.map((member) => (
              <li key={member.member_id} className="flex items-center justify-between py-3">
                <span className="text-sm">{memberNames.get(member.member_id) ?? member.member_id}</span>
                <time
                  dateTime={member.last_active}
                  title={formatDateTime(member.last_active)}
                  className="text-xs text-muted-foreground"
                >
                  {formatRelativeTime(member.last_active)}
                </time>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link
        to={`/${orgSlug}/projects/${projectSlug}/graph`}
        className="block rounded-lg border bg-card transition-colors hover:bg-accent/50"
      >
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Network className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">
                {t("projectPortal.overview.graphHUD", "Knowledge Graph")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("projectPortal.overview.graphHUDHint", "View nodes, edges and freshness")}
              </p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </Link>
    </div>
  );
}
